import { useEffect, useState } from 'react';
import nacanacaLogo from '../../../../assets/logo.webp';
import devilIllustration from '../../../../assets/diablo.webp';
import nohoLogo from '../../../../assets/LOGONOHO.gif';

export const FESTIVAL_LINKS = [
  { label: 'Instagram', handle: '@nacanacafestival', url: 'https://www.instagram.com/nacanacafestival' },
  { label: 'Facebook', handle: 'Ñaca Ñaca Festival', url: 'https://www.facebook.com/nacanacafestival' },
];
export const NOHO_LINKS = [
  { label: 'Instagram', url: 'https://www.instagram.com/noho.love' },
  { label: 'Facebook', url: 'https://www.facebook.com/lovenoho' },
  { label: 'TikTok', url: 'https://www.tiktok.com/@noholov' },
  { label: 'WhatsApp', url: 'https://wa.me/527224730002' },
];
const NOHO_URL = 'https://lovenoho.com/';

const delay = (seconds) => ({ '--d': `${seconds}s` });
const number = (value) => (Number(value) || 0).toLocaleString('es-MX');
const plural = (value, one, many) => (Number(value) === 1 ? one : many);

// Conteo ascendente breve: solo vive mientras la diapositiva está visible.
function CountUp({ value, durationMs = 1400, startDelayMs = 350 }) {
  const target = Math.max(0, Number(value) || 0);
  const [shown, setShown] = useState(0);
  useEffect(() => {
    let frameId = 0;
    let startedAt = 0;
    const tick = (now) => {
      if (!startedAt) startedAt = now;
      const progress = Math.min(1, (now - startedAt) / durationMs);
      setShown(Math.round(target * (1 - (1 - progress) ** 3)));
      if (progress < 1) frameId = window.requestAnimationFrame(tick);
    };
    const timeoutId = window.setTimeout(() => { frameId = window.requestAnimationFrame(tick); }, startDelayMs);
    return () => {
      window.clearTimeout(timeoutId);
      window.cancelAnimationFrame(frameId);
    };
  }, [target, durationMs, startDelayMs]);
  return <>{shown.toLocaleString('es-MX')}</>;
}

function Eyebrow({ children, at = 0 }) {
  return (
    <p className="rv-in text-[11px] font-black uppercase tracking-[.3em] text-[#F7E04B]" style={delay(at)}>
      {children}
    </p>
  );
}

function Headline({ children, at = 0.15, className = '' }) {
  return (
    <h2
      className={`rv-in mt-3 font-display text-[2.1rem] uppercase leading-[1.08] text-white sm:text-5xl ${className}`}
      style={delay(at)}
    >
      {children}
    </h2>
  );
}

function BigNumber({ value, at = 0.3, suffix = '' }) {
  return (
    <p
      className="rv-pop rv-glow mt-4 font-display text-[4.6rem] leading-none text-[#F7E04B] sm:text-[6.5rem]"
      style={delay(at)}
    >
      <CountUp value={value} startDelayMs={at * 1000 + 250} />{suffix}
    </p>
  );
}

function Fact({ value, label, at }) {
  return (
    <div className="rv-in border border-white/20 bg-black/45 px-3 py-4" style={delay(at)}>
      <p className="font-display text-3xl leading-none text-[#F7E04B]">{value}</p>
      <p className="mt-2 text-[10px] font-black uppercase leading-4 tracking-[.14em] text-white/85">{label}</p>
    </div>
  );
}

function Body({ children, at = 0.6 }) {
  return (
    <p className="rv-in mx-auto mt-5 max-w-sm text-base leading-7 text-[#E9DDF7]" style={delay(at)}>
      {children}
    </p>
  );
}

function LinkPill({ href, children, tone = 'light' }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={[
        'inline-flex min-h-[44px] items-center justify-center rounded-full border px-3 py-2',
        'text-[11px] font-black uppercase tracking-[.12em] transition-transform active:scale-95',
        tone === 'light'
          ? 'border-[#F7E04B] bg-[#F7E04B] text-black'
          : 'border-white/45 bg-black/50 text-white',
      ].join(' ')}
    >
      {children}
    </a>
  );
}

function rankLine(review) {
  if (!review.rank) return 'La maldición te espera en la próxima edición.';
  if (review.rank === 1) return '¡Primer lugar! Toluca entera baila a tu ritmo.';
  if (review.rank <= 3) return `¡Podio! Lugar #${review.rank} de ${number(review.totalPlayers)} almas.`;
  if (review.rank <= 10) return `Top 10: lugar #${review.rank} de ${number(review.totalPlayers)} almas.`;
  return `Lugar #${number(review.rank)} de ${number(review.totalPlayers)} almas que jugaron.`;
}

// Cada diapositiva: fondo (dos colores), duración y contenido. Las que no
// tienen datos del jugador se omiten para que el recuento siempre sea suyo.
export function buildReviewSlides({ review, username, onReplay, onClose }) {
  const data = review || {};
  const played = (data.gamesPlayed || 0) > 0 || (data.totalPoints || 0) > 0;
  const slides = [];

  slides.push({
    id: 'hola',
    colors: ['#7C3AED', '#0A1A0F'],
    content: (
      <>
        <img
          src={nacanacaLogo}
          alt="Ñaca Ñaca"
          className="rv-pop rv-float-img mx-auto w-40 sm:w-48"
          style={delay(0)}
        />
        <Eyebrow at={0.4}>02 · 03 · 04 de octubre</Eyebrow>
        <Headline at={0.6}>Tres noches.<br />Una maldición.<br />Y tú.</Headline>
        <Body at={1.2}>
          Toluca tembló a ritmo de Ñaca Ñaca, <strong className="text-[#F7E04B]">@{username}</strong>.
          Antes de apagar las velas, mira todo lo que hiciste.
        </Body>
      </>
    ),
  });

  if (played) {
    slides.push({
      id: 'puntos',
      chime: true,
      colors: ['#B45309', '#3B0764'],
      content: (
        <>
          <Eyebrow>Tu maldición en números</Eyebrow>
          <Headline>Cerraste el festival con</Headline>
          <BigNumber value={data.totalPoints} at={0.5} />
          <p className="rv-in mt-2 text-sm font-black uppercase tracking-[.22em] text-white" style={delay(0.9)}>
            puntos oficiales
          </p>
          <Body at={1.5}>{rankLine(data)}</Body>
        </>
      ),
    });

    slides.push({
      id: 'partidas',
      colors: ['#15803D', '#1E1B4B'],
      content: (
        <>
          <Eyebrow>No soltaste el ritmo</Eyebrow>
          <Headline>
            Jugaste {number(data.gamesPlayed)} {plural(data.gamesPlayed, 'partida', 'partidas')}
          </Headline>
          <div className="mt-6 grid grid-cols-3 gap-2">
            <Fact value={number(data.minutesPlayed)} label="minutos de ritmo" at={0.6} />
            <Fact value={number(data.totalHits)} label="señales acertadas" at={0.8} />
            <Fact value={number(data.wins)} label={plural(data.wins, 'victoria', 'victorias')} at={1} />
          </div>
          <Body at={1.5}>
            {data.perfectHits > 0
              ? `${number(data.perfectHits)} de esos golpes fueron perfectos. Ni la muerte lleva tan bien el compás.`
              : 'Cada toque mantuvo viva la maldición.'}
          </Body>
        </>
      ),
    });

    slides.push({
      id: 'dias',
      colors: ['#BE185D', '#0A1A0F'],
      content: (
        <>
          <Eyebrow>Volviste por más</Eyebrow>
          <Headline>
            {number(data.daysActive)} {plural(data.daysActive, 'día', 'días')} bajo la maldición
          </Headline>
          <div className="mt-6 grid grid-cols-2 gap-2">
            <Fact
              value={`${number(data.longestDayStreak)} ${plural(data.longestDayStreak, 'día', 'días')}`}
              label="tu racha más larga seguida"
              at={0.6}
            />
            <Fact value={`${number(data.zonesVisited)}/5`} label="zonas de Toluca maldecidas" at={0.8} />
          </div>
          {data.favoriteZone && (
            <Body at={1.3}>
              Tu escenario favorito: <strong className="text-[#F7E04B]">{data.favoriteZone}</strong>.
            </Body>
          )}
        </>
      ),
    });

    slides.push({
      id: 'mejores',
      chime: true,
      colors: ['#1D4ED8', '#4C1D95'],
      content: (
        <>
          <Eyebrow>Tus mejores momentos</Eyebrow>
          <Headline>Cuando nadie te pudo parar</Headline>
          <div className="mt-6 grid grid-cols-2 gap-2">
            <Fact value={number(data.bestScore)} label="mejor puntaje en una partida" at={0.5} />
            <Fact value={`x${number(data.bestCombo)}`} label="combo más largo" at={0.7} />
            <Fact value={`${number(data.bestStreakSec)} s`} label="racha más larga sin fallar" at={0.9} />
            <Fact
              value={data.averageAccuracy == null ? '—' : `${data.averageAccuracy}%`}
              label="precisión promedio"
              at={1.1}
            />
          </div>
          {data.bestWinStreak > 1 && (
            <Body at={1.6}>Llegaste a ganar {number(data.bestWinStreak)} partidas seguidas.</Body>
          )}
        </>
      ),
    });

    if (data.hiddenFinds > 0) {
      slides.push({
        id: 'fantasmas',
        colors: ['#0E7490', '#1E1B4B'],
        content: (
          <>
            <p className="rv-pop rv-float-img text-7xl" style={delay(0)} aria-hidden="true">👻</p>
            <Eyebrow at={0.3}>Ojos de tecolote</Eyebrow>
            <Headline at={0.45}>Atrapaste</Headline>
            <BigNumber value={data.hiddenFinds} at={0.7} />
            <p className="rv-in mt-2 text-sm font-black uppercase tracking-[.22em] text-white" style={delay(1.1)}>
              {plural(data.hiddenFinds, 'fantasmita escondido', 'fantasmitas escondidos')}
            </p>
            <Body at={1.6}>Te dejaron {number(data.hiddenPoints)} puntos antes de desvanecerse en el mapa.</Body>
          </>
        ),
      });
    }

    if (data.duelsSent > 0 || data.duelsCompleted > 0) {
      slides.push({
        id: 'duelos',
        colors: ['#B91C1C', '#111827'],
        content: (
          <>
            <Eyebrow>No jugaste a solas</Eyebrow>
            <Headline>
              {data.peopleInvited > 0
                ? `Invitaste a ${number(data.peopleInvited)} ${plural(data.peopleInvited, 'persona', 'personas')} a la maldición`
                : 'Aceptaste el duelo'}
            </Headline>
            <div className="mt-6 grid grid-cols-3 gap-2">
              <Fact value={number(data.duelsSent)} label="retos lanzados" at={0.6} />
              <Fact value={number(data.duelsCompleted)} label="duelos completados" at={0.8} />
              <Fact value={number(data.duelsWon)} label="duelos ganados" at={1} />
            </div>
            <Body at={1.5}>
              {data.rivals > 0
                ? `Te mediste con ${number(data.rivals)} ${plural(data.rivals, 'rival distinto', 'rivales distintos')}. `
                  + 'Compartir el susto también es ganar.'
                : 'Compartir el susto también es ganar.'}
            </Body>
          </>
        ),
      });
    }

    slides.push({
      id: 'medallas',
      colors: ['#A16207', '#14532D'],
      content: (
        <>
          <Eyebrow>Tu salón de medallas</Eyebrow>
          <Headline>
            {number(data.medalsUnlocked)} de {number(data.medalsTotal)} medallas
          </Headline>
          {data.topMedals?.length > 0 && (
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {data.topMedals.map((medal, index) => (
                <span
                  key={medal.id}
                  className={[
                    'rv-pop inline-flex items-center gap-2 rounded-full border border-[#F7E04B]/70 bg-black/55',
                    'px-3 py-2 text-[11px] font-black uppercase tracking-[.08em] text-white',
                  ].join(' ')}
                  style={delay(0.5 + index * 0.15)}
                >
                  <span aria-hidden="true">{medal.icon}</span>{medal.title}
                </span>
              ))}
            </div>
          )}
          {data.tournaments?.length > 0 && (
            <Body at={1.6}>
              🏆 En «{data.tournaments[data.tournaments.length - 1].name}» quedaste en el lugar
              {' '}#{number(data.tournaments[data.tournaments.length - 1].position)}
              {' '}de {number(data.tournaments[data.tournaments.length - 1].participants)}.
            </Body>
          )}
          {!data.tournaments?.length && <Body at={1.6}>Cada una cuenta una parte de tu historia en el festival.</Body>}
        </>
      ),
    });
  }

  slides.push({
    id: 'gracias',
    chime: true,
    durationMs: 9000,
    colors: ['#7C3AED', '#831843'],
    wide: true,
    content: (
      <div className="rv-split rv-split-hero">
        <img src={devilIllustration} alt="" className="rv-hero rv-pop rv-float-img mx-auto" style={delay(0)} />
        <div>
          <Headline at={0.5} className="rv-glow !text-[#F7E04B]">
            Gracias por jugar <span className="rv-heart" aria-hidden="true">💜</span>
          </Headline>
          <Body at={1.1}>
            {played
              ? 'Este juego existió por tres noches, pero lo que lo hizo especial fuiste tú: cada toque, '
                + 'cada duelo y cada fantasma atrapado mantuvieron viva la maldición.'
              : 'No alcanzaste a jugar esta vez, pero la maldición ya sabe tu nombre. Te esperamos en la próxima edición.'}
          </Body>
          <Body at={1.9}>El festival termina hoy. El ritmo se queda contigo, @{username}.</Body>
        </div>
      </div>
    ),
  });

  slides.push({
    id: 'creditos',
    durationMs: 13000,
    colors: ['#1E1B4B', '#0A1A0F'],
    align: 'top',
    content: (
      <div className="rv-credits space-y-9 text-center">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[.3em] text-[#F7E04B]">Agradecimientos</p>
          <p className="mx-auto mt-3 max-w-xs text-base leading-7 text-white">
            A quienes jugaron, compartieron duelos y maldijeron Toluca con ritmo.
            Al público del festival, a las sedes y a cada persona que corrió la voz.
          </p>
        </div>
        <div>
          <p className="text-[11px] font-black uppercase tracking-[.3em] text-[#7EC54A]">Festival</p>
          <p className="mt-3 font-display text-2xl leading-8 text-white">Ñaca Ñaca</p>
          <p className="mx-auto max-w-xs text-sm leading-6 text-[#C5E6A0]">
            Festival Internacional de Cine de Terror de la Ciudad de Toluca · 3ra edición
          </p>
          <p className="mt-1 text-sm text-white">Equipo Ñaca Ñaca</p>
        </div>
        <div>
          <p className="text-[11px] font-black uppercase tracking-[.3em] text-[#F7E04B]">La Maldición Ñaca Ñaca</p>
          <p className="mt-3 text-[10px] font-black uppercase tracking-[.2em] text-white/70">
            Dirección, diseño, ritmo y desarrollo
          </p>
          <p className="font-display text-2xl text-white">NOHO</p>
          <p className="mt-4 text-[10px] font-black uppercase tracking-[.2em] text-white/70">Personajes y ambiente</p>
          <p className="text-base text-white">Equipo Ñaca Ñaca y NOHO</p>
          <p className="mt-4 text-[10px] font-black uppercase tracking-[.2em] text-white/70">Música de ambientación</p>
          <p className="text-base text-white">Ximena Contreras</p>
          <p className="mt-4 text-[10px] font-black uppercase tracking-[.2em] text-white/70">Música de juego</p>
          <p className="text-base text-white">Misael Padilla Sauceda</p>
        </div>
        <div>
          <p className="text-[10px] font-black uppercase tracking-[.2em] text-white/70">Y en el papel protagónico</p>
          <p className="rv-glow font-display text-3xl text-[#F7E04B]">@{username}</p>
        </div>
      </div>
    ),
  });

  slides.push({
    id: 'final',
    isStatic: true,
    colors: ['#3B0764', '#0A1A0F'],
    align: 'scroll',
    wide: true,
    content: (
      <div className="rv-split">
        <div>
        <img src={nacanacaLogo} alt="Ñaca Ñaca" className="rv-pop mx-auto w-20" style={delay(0)} />
        <Headline at={0.3} className="!mt-1 !text-[1.6rem] sm:!text-4xl">La maldición sigue en redes</Headline>
        <p className="rv-in mx-auto mt-2 max-w-sm text-sm leading-5 text-[#E9DDF7]" style={delay(0.6)}>
          Sigue a <strong className="text-white">Ñaca Ñaca Fest</strong> ({FESTIVAL_LINKS[0].handle}) para
          enterarte de la próxima edición.
        </p>
        <div className="rv-in mt-3 flex flex-wrap justify-center gap-2" style={delay(0.8)}>
          {FESTIVAL_LINKS.map((link) => (
            <LinkPill key={link.url} href={link.url}>{link.label}</LinkPill>
          ))}
        </div>
        </div>

        <div className="rv-in mx-auto mt-4 w-full max-w-sm border border-white/20 bg-black/50 p-3" style={delay(1.1)}>
          <p className="text-[10px] font-black uppercase tracking-[.22em] text-[#7EC54A]">
            ¿Quieres una experiencia así?
          </p>
          <a href={NOHO_URL} target="_blank" rel="noopener noreferrer" aria-label="Visitar lovenoho.com">
            <img src={nohoLogo} alt="NOHO" className="mx-auto mt-2 h-7 w-auto" />
          </a>
          <p className="mt-2 text-[13px] leading-5 text-white">
            Este juego lo creó NOHO. Si quieres algo así para tu marca, festival o evento, visita{' '}
            <a
              href={NOHO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-black text-[#F7E04B] underline underline-offset-4"
            >
              lovenoho.com
            </a>.
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-1.5">
            {NOHO_LINKS.map((link) => (
              <LinkPill key={link.url} href={link.url} tone="dark">{link.label}</LinkPill>
            ))}
          </div>
        </div>

        <div className="rv-split-full">
        <p className="rv-in mx-auto mt-4 max-w-sm text-sm font-bold leading-6 text-white" style={delay(1.4)}>
          Gracias por tanto, @{username}. Nos vemos en la próxima maldición{' '}
          <span className="rv-heart" aria-hidden="true">💜</span>
        </p>
        <div className="rv-in mx-auto mt-3 grid max-w-sm grid-cols-2 gap-2" style={delay(1.6)}>
          <button
            type="button"
            onClick={onReplay}
            className={[
              'min-h-[48px] rounded-sm border border-white/45 bg-black/50 px-3 py-3 text-[11px]',
              'font-black uppercase tracking-[.12em] text-white',
            ].join(' ')}
          >
            Ver de nuevo
          </button>
          <button
            type="button"
            onClick={onClose}
            className={[
              'min-h-[48px] rounded-sm border border-[#F7E04B] bg-[#7C3AED] px-3 py-3 text-[11px]',
              'font-black uppercase tracking-[.12em] text-white shadow-[4px_4px_0_#0A1A0F]',
            ].join(' ')}
          >
            Volver al mapa
          </button>
        </div>
        </div>
      </div>
    ),
  });

  return slides;
}
